import { CheckCircle2, MonitorDown } from "lucide-react";
import { useState } from "react";
import { useBranding } from "../lib/branding";
import { browserKind, useInstall } from "../lib/install";
import { Button, Modal, cx } from "./ui";

/** Sidebar item: "Install app" (hidden once running as the installed app). */
export function InstallAppButton({ className }: { className?: string }) {
  const { installed } = useInstall();
  const [open, setOpen] = useState(false);
  if (installed) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cx("flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-brand-800 hover:bg-brand-50", className)}
      >
        <MonitorDown className="size-5 shrink-0" aria-hidden /> Install app
      </button>
      {open && <InstallAppModal onClose={() => setOpen(false)} />}
    </>
  );
}

function Steps({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
      {items.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ol>
  );
}

const Key = ({ children }: { children: React.ReactNode }) => (
  <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-xs">{children}</kbd>
);

export function InstallAppModal({ onClose }: { onClose: () => void }) {
  const { productName } = useBranding();
  const { canPrompt, prompt, installed } = useInstall();
  const [done, setDone] = useState(false);
  const browser = browserKind();
  const isWindows = /Windows/.test(navigator.userAgent);
  return (
    <Modal open wide title={`Install ${productName} on this computer`} onClose={onClose} footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      <div className="space-y-5">
        <p className="text-sm text-slate-700">
          The app opens in its own window with an icon on the desktop and taskbar, like any program. It is the same system as this website, always
          up to date, and nothing extra is stored on the computer: records stay safely on the server.
        </p>

        {done || installed ? (
          <p className="flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">
            <CheckCircle2 className="size-5" aria-hidden /> Installed. Open it from the desktop or taskbar icon from now on.
          </p>
        ) : canPrompt ? (
          <div className="rounded-lg bg-brand-50 p-4">
            <Button onClick={async () => setDone(await prompt())}>
              <MonitorDown className="size-4" aria-hidden /> Install now
            </Button>
            {browser === "edge" && (
              <p className="mt-2 text-sm text-slate-700">
                In the window Edge shows next, tick <strong>Pin to taskbar</strong>, <strong>Create desktop shortcut</strong> and{" "}
                <strong>Auto-start on device login</strong> so it opens every time the computer starts.
              </p>
            )}
          </div>
        ) : (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900">How to install</h3>
            {browser === "edge" || browser === "chrome" ? (
              <Steps
                items={[
                  <>Look for the install icon (a small screen with an arrow) at the right end of the address bar and click it, then <strong>Install</strong>.</>,
                  <>
                    Or open the browser menu <Key>⋯</Key> → {browser === "edge" ? <strong>Apps → Install this site as an app</strong> : <strong>Cast, save and share → Install page as app</strong>}.
                  </>,
                ]}
              />
            ) : browser === "safari" ? (
              <Steps items={[<>Mac: in Safari choose <strong>File → Add to Dock</strong>.</>, <>iPhone / iPad: tap <strong>Share → Add to Home Screen</strong>.</>]} />
            ) : (
              <p className="text-sm text-slate-700">
                This browser cannot install apps. Open this page in <strong>Microsoft Edge</strong> or <strong>Google Chrome</strong> and choose Install app.
              </p>
            )}
          </section>
        )}

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Open it automatically when the computer starts</h3>
          {browser === "edge" && (
            <Steps
              items={[
                <>Open Edge and go to <Key>edge://apps</Key>.</>,
                <>On the {productName} app, click <Key>⋯</Key> → <strong>Auto-start on device login</strong>.</>,
              ]}
            />
          )}
          {browser === "chrome" && (
            <Steps
              items={[
                <>Open Chrome and type <Key>chrome://apps</Key> in the address bar.</>,
                <>Right-click the {productName} icon → <strong>Start app when you sign in</strong>.</>,
              ]}
            />
          )}
          {isWindows && (
            <>
              <p className="text-sm text-slate-600">Works with any browser on Windows:</p>
              <Steps
                items={[
                  <>Press <Key>Windows</Key> + <Key>R</Key>, type <Key>shell:startup</Key> and press Enter. A folder opens.</>,
                  <>Copy the {productName} icon from the desktop into that folder.</>,
                ]}
              />
            </>
          )}
          {!isWindows && browser !== "edge" && browser !== "chrome" && (
            <p className="text-sm text-slate-700">On a Mac: right-click the app in the Dock → Options → <strong>Open at Login</strong>.</p>
          )}
        </section>

        <p className="text-xs text-slate-500">
          Each person signs in once on the computer. On a shared reception computer, sign out at the end of the day from the menu.
        </p>
      </div>
    </Modal>
  );
}
